import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ReviewStats from './ReviewStats';
import ReviewCard from './ReviewCard';
import supabase from '../../lib/supabaseClient';

const Reviews = ({ reviews, moverData }) => {
    const [tab, setTab] = useState('all'); // all | feedback
    const [filter, setFilter] = useState('all'); // all | 5 | 4 | 3
    const [showGoogleModal, setShowGoogleModal] = useState(false);
    const [connectedGoogle, setConnectedGoogle] = useState(false);
    const [realReviews, setRealReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [moverId, setMoverId] = useState(null);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [submittingReply, setSubmittingReply] = useState(false);

    // Set mover ID from props
    useEffect(() => {
        if (moverData?.id) {
            setMoverId(moverData.id);
        }
    }, [moverData]);

    // Fetch real reviews from database
    useEffect(() => {
        const fetchReviews = async () => {
            try {
                setLoading(true);
                
                if (!moverId) {
                    setRealReviews([]);
                    setLoading(false);
                    return;
                }
                
                const { data, error } = await supabase
                    .from('mover_reviews')
                    .select('*')
                    .eq('mover_id', moverId)
                    .order('created_at', { ascending: false });

                if (error) {
                    setRealReviews([]);
                } else {
                    setRealReviews(data || []);
                }
            } catch (error) {
                setRealReviews([]);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, [moverId]);

    const handleSubmitReply = async (reviewId) => {
        if (!replyText.trim()) {
            toast.error('Please enter a reply');
            return;
        }

        try {
            setSubmittingReply(true);
            
            const { error } = await supabase
                .from('mover_reviews')
                .update({ 
                    mover_reply: replyText,
                    mover_reply_date: new Date().toISOString()
                })
                .eq('id', reviewId);

            if (error) throw error;

            // Update local state
            setRealReviews(prev => prev.map(r => 
                r.id === reviewId 
                    ? { ...r, mover_reply: replyText, mover_reply_date: new Date().toISOString() }
                    : r
            ));

            toast.success('Reply submitted successfully!');
            setReplyingTo(null);
            setReplyText('');
        } catch (error) {
            toast.error('Failed to submit reply. Please try again.');
        } finally {
            setSubmittingReply(false);
        }
    };

    // Use only real reviews (remove mock reviews)
    const filtered = realReviews.filter(r => filter === 'all' || r.rating === Number(filter));

    return (
        <div>
            {/* Header */}
            <div className="mb-4">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Reviews</h1>
                <p className="text-sm text-gray-600">Manage customer reviews and requests</p>
            </div>

            {/* Sub Tabs (request tab removed) */}
            <div className="mb-6 border-b border-gray-200">
                <div className="flex gap-6 overflow-x-auto">
                    <button onClick={()=>setTab('all')} className={`py-2 border-b-2 -mb-px font-medium ${tab==='all'?'border-orange-500 text-orange-600':'border-transparent text-gray-600 hover:text-gray-800'}`}>All reviews</button>
                    <button onClick={()=>setTab('feedback')} className={`py-2 border-b-2 -mb-px font-medium ${tab==='feedback'?'border-orange-500 text-orange-600':'border-transparent text-gray-600 hover:text-gray-800'}`}>Feedback</button>
                </div>
            </div>

            {tab === 'all' && (
                <div>
                    <ReviewStats reviews={realReviews} />

                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-800">Customer reviews</h2>
                        <div className="flex items-center gap-2">
                            {['all','5','4','3'].map(f => (
                                <button key={f} onClick={()=>setFilter(f)} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${filter===f?'bg-white border border-gray-300 text-gray-800':'text-gray-600 hover:text-gray-800 hover:bg-gray-100'}`}>
                                    {f==='all'?'All':`${f}★`}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                            <p className="text-gray-600 mt-4">Loading reviews...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="bg-white rounded-xl border p-8 text-center">
                            <div className="text-4xl mb-4">⭐</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews yet</h3>
                            <p className="text-gray-600">Your customer reviews will appear here once customers submit them.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filtered.map((review, index) => (
                                <div key={review.id || index} className="bg-white rounded-xl border p-5">
                                    {/* Review Header */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                                                        {(review.reviewer_name || review.name || 'C').charAt(0).toUpperCase()}
                                                    </div>
                                                    <h4 className="font-semibold text-gray-900">
                                                        {review.reviewer_name || review.name || 'Customer'}
                                                    </h4>
                                                </div>
                                                {review.is_verified && (
                                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full border border-blue-200">
                                                        ✓ Verified
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-yellow-500 text-lg">
                                                    {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                                                </span>
                                                <span className="text-sm text-gray-600">({review.rating}/5)</span>
                                            </div>
                                        </div>
                                        <div className="text-right text-sm text-gray-500">
                                            {review.created_at ? new Date(review.created_at).toLocaleDateString() : review.date}
                                        </div>
                                    </div>

                                    {/* Review Text */}
                                    <div className="mb-4">
                                        <p className="text-gray-700">{review.review_text || review.text}</p>
                                    </div>

                                    {/* Mover Reply Section */}
                                    {review.mover_reply && (
                                        <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4 mb-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-sm font-semibold text-blue-900">Your Reply:</span>
                                                <span className="text-xs text-blue-600">
                                                    {new Date(review.mover_reply_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 text-sm">{review.mover_reply}</p>
                                            <button
                                                onClick={() => {
                                                    setReplyingTo(review.id);
                                                    setReplyText(review.mover_reply);
                                                }}
                                                className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                            >
                                                Edit Reply
                                            </button>
                                        </div>
                                    )}

                                    {/* Reply Form */}
                                    {replyingTo === review.id ? (
                                        <div className="border-2 border-blue-200 rounded-lg p-4 bg-white">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {review.mover_reply ? 'Edit Your Reply:' : 'Reply to this review:'}
                                            </label>
                                            <textarea
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                placeholder="Thank your customer and address their feedback..."
                                                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-none"
                                                maxLength={500}
                                            />
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs text-gray-500">{replyText.length}/500 characters</span>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleSubmitReply(review.id)}
                                                        disabled={submittingReply || !replyText.trim()}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                                                    >
                                                        {submittingReply ? 'Submitting...' : 'Submit Reply'}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setReplyingTo(null);
                                                            setReplyText('');
                                                        }}
                                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : !review.mover_reply && review.id && (
                                        <button
                                            onClick={() => setReplyingTo(review.id)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                        >
                                            Reply to Review
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Request reviews tab removed */}

            {tab === 'feedback' && (
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border p-4">
                        <div className="font-semibold text-gray-800 mb-1">Why great reviews matter</div>
                        <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                            <li>Pros with 3+ reviews are more likely to be hired.</li>
                            <li>Asking early increases your odds of winning jobs.</li>
                        </ul>
                    </div>
                    {/* Google reviews section added here */}
                    <div className="bg-white rounded-xl border p-4">
                        <div className="font-semibold text-gray-800 mb-2">Google reviews</div>
                        {connectedGoogle ? (
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm text-green-700">Connected to Google. Import your latest reviews below.</div>
                                <button onClick={()=>setShowGoogleModal(true)} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">Import reviews</button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="text-sm text-gray-600">Connect your Google Business Profile to import recent reviews.</div>
                                <button onClick={()=>setShowGoogleModal(true)} className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.443,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.277-7.949l-6.563,5.046C9.554,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-3.983,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.271,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg>
                                    Connect Google
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="bg-white rounded-xl border p-4">
                        <div className="font-semibold text-gray-800 mb-2">Recent feedback</div>
                        <div className="text-sm text-gray-600">No feedback yet.</div>
                    </div>
                </div>
            )}

            {/* Confirm modal removed with request reviews */}

            {/* Google connect modal (mock sign-in) */}
            {showGoogleModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={()=>setShowGoogleModal(false)}>
                    <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e)=>e.stopPropagation()}>
                        <div className="text-lg font-semibold text-gray-800 mb-2">Connect Google Business Profile</div>
                        <p className="text-sm text-gray-600 mb-4">Sign in with Google to import feedback and reviews.</p>
                        <button onClick={()=>{setConnectedGoogle(true); setShowGoogleModal(false);}} className="w-full flex items-center justify-center gap-2 border rounded-lg py-2 hover:bg-gray-50">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.443,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.277-7.949l-6.563,5.046C9.554,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-3.983,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.271,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg>
                            Sign in with Google
                        </button>
                        <div className="mt-3 text-xs text-gray-500">Note: This is a placeholder sign-in. Provide your Google login ID later for real integration.</div>
                        <div className="mt-4 flex justify-end">
                            <button onClick={()=>setShowGoogleModal(false)} className="text-sm text-gray-600 hover:text-gray-800">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reviews;
